import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { saveAs } from 'file-saver';
import { PDFDocumentProxy } from 'ng2-pdf-viewer';
import { BehaviorSubject, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { SnackBarConfig } from '../../../shared/configs';
import { ShipstationApiService } from '../../../shared/services';
import { trackById } from '../../../shared/util';
import { EPackingSlipPageSize, IShipstationShipment } from '../../interfaces';

@Component({
  selector: 'lum-shipping-label-details',
  templateUrl: './shipping-label-details.component.html',
  styleUrls: ['./shipping-label-details.component.scss'],
})
export class ShippingLabelDetailsComponent implements OnInit, OnDestroy {
  pdfSrc :Uint8Array;
  rotationAngle :number = 0;
  zoomValue :number = 1;
  shipment :IShipstationShipment | null = null;
  selectedTabIndex :number = 0;
  base64pdf :string;
  ePageSize = EPackingSlipPageSize;
  packingSlipPageSize :string = EPackingSlipPageSize.A4;

  trackById = trackById;

  private pdf :PDFDocumentProxy;

  private readonly _loading = new BehaviorSubject<boolean>(<boolean>false);
  loading$ = this._loading.asObservable();

  private readonly _loadingPdf = new BehaviorSubject<boolean>(<boolean>false);
  loadingPdf$ = this._loadingPdf.asObservable();

  private onDestroy$ = new Subject();

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data :{
      salesOrderId :number;
      withPackingSlip :boolean;
    },
    private shipstationApiService :ShipstationApiService,
    private dialogRef :MatDialogRef<ShippingLabelDetailsComponent>,
    private snackbar :MatSnackBar,
  ) {}

  ngOnInit() :void {
    this.loadShipments();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onClose() :void {
    this.dialogRef.close();
  }

  onPdfLoaded(pdf :PDFDocumentProxy) :void {
    this.pdf = pdf;
  }

  onCreateLabel() :void {
    this.selectedTabIndex = 1;
    if (this.data.withPackingSlip) {
      this.loadShipmentsWithPackingSlip();
    }
  }

  onRotate(degrees :number) :void {
    this.rotationAngle = (this.rotationAngle + degrees + 360) % 360;
  }

  onZoom(value :number) :void {
    const newZoomValue = this.zoomValue + value;

    if (newZoomValue >= 0.5 && newZoomValue <= 2.0) {
      this.zoomValue = newZoomValue;
    }
  }

  onDownload() :void {
    if (this.pdfSrc) {
      const pdfBlob = new Blob([this.pdfSrc], { type: 'application/pdf' });
      const filename = this.shipment?.salesOrderId + '-shipping-label.pdf';
      saveAs(pdfBlob, filename);
    }
  }

  onPrint() :void {
    this.pdf.getData().then((u8) => {
      const blob = new Blob([u8.buffer], {
        type: 'application/pdf',
      });

      const blobUrl = window.URL.createObjectURL(blob);
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = blobUrl;
      document.body.appendChild(iframe);
      iframe.contentWindow?.print();
    });
  }

  private loadShipments() :void {
    this._loading.next(true);
    this.shipstationApiService
      .getShipments({
        orderId: this.data.salesOrderId.toString(),
        relations: 'shippingCarrier',
      })
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          this._loading.next(false);
        }),
      )
      .subscribe({
        next: (response :any) => {
          const shipments = response.data?.filter(
            (shipment :IShipstationShipment) => shipment.labelData,
          );
          this.shipment = shipments.length ? shipments[0] : null;

          if (!this.data.withPackingSlip) {
            this.initPdfSrc();
          }
        },
        error: (error) => {
          this.snackbar.open('Error loading shipments.', 'Error', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  private loadShipmentsWithPackingSlip() :void {
    this._loadingPdf.next(true);
    this.shipstationApiService
      .getLabelWithPackingSlip(
        this.data.salesOrderId.toString(),
        this.packingSlipPageSize,
      )
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          this._loadingPdf.next(false);
        }),
      )
      .subscribe({
        next: (response :any) => {
          this.base64pdf = response.base64pdf;
          this.initPdfSrc();
        },
        error: (error) => {
          this.snackbar.open(
            'Error loading shipments with packing slip.',
            'Error',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );
        },
      });
  }

  private initPdfSrc() :void {
    if (this.data.withPackingSlip) {
      this.pdfSrc = this.base64ToArrayBuffer(this.base64pdf);

      return;
    }

    if (!this.shipment?.labelData) {
      return;
    }

    this.pdfSrc = this.base64ToArrayBuffer(this.shipment?.labelData);
  }

  private base64ToArrayBuffer(base64 :string) :Uint8Array {
    const binary_string = window.atob(base64);
    const len = binary_string.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binary_string.charCodeAt(i);
    }

    return bytes;
  }

  get total() :{ packages :number; shipmentCost :number } {
    return {
      packages: this.shipment ? 1 : 0,
      shipmentCost: this.shipment?.shipmentCost || 0,
    };
  }

  get pageTitle() :string {
    return this.selectedTabIndex ? 'Create Label Status' : 'Create Label Progress';
  }
}
